import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Line } from 'react-chartjs-2'
import TelegramIcon from '@mui/icons-material/Telegram'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import AssignedSearch from '../../../components/new_component/AssignedSearch'
import Api from '../../../Api'
import { operatorReportStyles as opStyles, greenTheme } from '../../../helpers/style'

// Demo data for development / fallback when API fails or returns empty
// provider 3 or 4 = Telegram, 7 = WhatsApp (for icon display)
const DEMO_MAIN_NUMBERS = [
  { pref: '+374', did: '101234567', notes: 'Sales line (Telegram)', provider: 3 },
  { pref: '+374', did: '109876543', notes: 'Support (WhatsApp)', provider: 7 },
  { pref: '', did: '100', notes: 'Reception' },
  { pref: '+374', did: '102000001', notes: 'Telegram channel', provider: 4 },
  { pref: '+374', did: '102000002', notes: 'WhatsApp business', provider: 7 },
]
const DEMO_AUTO_NUMBERS = [
  { name: 'IVR Main Menu', dnumber: '200' },
  { name: 'After-hours group', dnumber: '201' },
]

// Distinct colors for each number (stable by index)
const NUMBER_COLORS = [
  '#7ebc0a', '#3498db', '#e74c3c', '#9b59b6', '#f39c12',
  '#1abc9c', '#e91e63', '#00bcd4', '#8d6e63', '#5c6bc0',
]

// Range type: one day → hours; up to 31 days → days; more → months
function getRangeType(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return null
  const start = new Date(dateFrom + 'T00:00:00')
  const end = new Date(dateTo + 'T23:59:59.999')
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
  const diffMs = end - start
  const diffDays = diffMs / (24 * 60 * 60 * 1000)
  if (diffDays < 1) return 'hour'
  if (diffDays <= 31) return 'day'
  return 'month'
}

// Generate array of date strings (YYYY-MM-DD) from dateFrom to dateTo inclusive
function getDaysInRange(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return []
  const start = new Date(dateFrom + 'T00:00:00')
  const end = new Date(dateTo + 'T00:00:00')
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return []
  const out = []
  const d = new Date(start)
  while (d <= end) {
    out.push(d.toISOString().slice(0, 10))
    d.setDate(d.getDate() + 1)
  }
  return out
}

// Generate array of { key: 'YYYY-MM', label: 'Jan 2025' } for date range
const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function getMonthsInRange(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return []
  const start = new Date(dateFrom + 'T00:00:00')
  const end = new Date(dateTo + 'T00:00:00')
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return []
  const out = []
  const d = new Date(start.getFullYear(), start.getMonth(), 1)
  const endFirst = new Date(end.getFullYear(), end.getMonth(), 1)
  while (d <= endFirst) {
    const y = d.getFullYear()
    const m = d.getMonth() + 1
    out.push({ key: `${y}-${String(m).padStart(2, '0')}`, label: `${MONTH_NAMES[m]} ${y}` })
    d.setMonth(d.getMonth() + 1)
  }
  return out
}

// Hour slot keys and labels for one-day view (0..23) — keys "0".."23" to match bucketValToHour
function getHourSlotsAndLabels() {
  const slots = Array.from({ length: 24 }, (_, i) => String(i))
  const labels = slots.map((s) => `${s.padStart(2, '0')}:00`)
  return { slots, labels }
}

// Total count for a graphic bucket (by_type or by_disp)
function getBucketCount(b) {
  if (b && typeof b.count === 'number') return b.count
  const byDisp = b?.by_disp?.buckets
  if (Array.isArray(byDisp)) return byDisp.reduce((s, x) => s + (Number(x.count) || 0), 0)
  const byType = b?.by_type?.buckets
  if (Array.isArray(byType)) return byType.reduce((s, x) => s + (Number(x.count) || 0), 0)
  return 0
}

// Normalize bucket val to YYYY-MM-DD for grouping
function bucketValToDateStr(val) {
  if (val == null) return ''
  const raw = String(val)
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return `${m[1]}-${m[2]}-${m[3]}`
  const d = new Date(val)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

// Normalize bucket val to hour index 0-23 (for one-day view)
function bucketValToHour(val) {
  if (val == null) return null
  const d = new Date(val)
  if (Number.isNaN(d.getTime())) return null
  return d.getUTCHours()
}

// Normalize bucket val to YYYY-MM for month grouping
function bucketValToMonthStr(val) {
  if (val == null) return ''
  const raw = String(val)
  const m = raw.match(/^(\d{4})-(\d{2})/)
  if (m) return `${m[1]}-${m[2]}`
  const d = new Date(val)
  return Number.isNaN(d.getTime()) ? '' : d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0')
}

export default function NumbersPage({
  opTr,
  langIndex,
  historyData,
  historyFacetFields,
  graphicStatusData,
  graphicDispositionData,
  historyGraphicLoading,
  historyGraphicError,
  report,
  calls,
  dateFrom,
  dateTo,
}) {
  const [mainNumbers, setMainNumbers] = useState([])
  const [autoNumbers, setAutoNumbers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isDemoData, setIsDemoData] = useState(false)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [chartDataLoading, setChartDataLoading] = useState(false)
  const [countsByItemBySlot, setCountsByItemBySlot] = useState({}) // { itemId: { slotKey: count } } — slotKey is hour "0"-"23", day "YYYY-MM-DD", or month "YYYY-MM"
  const defaultSelectionSet = useRef(false)

  const rangeType = useMemo(() => getRangeType(dateFrom, dateTo), [dateFrom, dateTo])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setIsDemoData(false)
    Api.getMainNumbers()
      .then((res) => {
        if (cancelled) return
        const data = res?.data || {}
        const mains = Array.isArray(data.mainNumbers) ? data.mainNumbers : []
        const autos = Array.isArray(data.autoNumbers) ? data.autoNumbers : []
        setMainNumbers(mains.length > 0 ? mains : DEMO_MAIN_NUMBERS)
        setAutoNumbers(autos.length > 0 ? autos : DEMO_AUTO_NUMBERS)
        if (mains.length === 0 && autos.length === 0) setIsDemoData(true)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || true)
          setMainNumbers(DEMO_MAIN_NUMBERS)
          setAutoNumbers(DEMO_AUTO_NUMBERS)
          setIsDemoData(true)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  const combinedList = useMemo(() => {
    const dids = (mainNumbers || []).map((row, idx) => ({
      type: 'did',
      id: `did-${([row.pref, row.did].filter(Boolean).join('') || row.did) || row.number || idx}`,
      label: row.notes != null ? String(row.notes).trim() : '',
      number: ([row.pref, row.did].filter(Boolean).join('') || row.did || row.number) || '',
      color: NUMBER_COLORS[idx % NUMBER_COLORS.length],
      provider: row.provider ?? row.profile,
    }))
    const groups = (autoNumbers || []).map((row, idx) => ({
      type: 'group',
      id: `group-${row.dnumber ?? row.item ?? row.short ?? idx}`,
      label: row.name ?? row.groupName ?? '',
      number: row.dnumber ?? row.item ?? row.short ?? '',
      color: NUMBER_COLORS[(dids.length + idx) % NUMBER_COLORS.length],
    }))
    return [...dids, ...groups]
  }, [mainNumbers, autoNumbers])

  useEffect(() => {
    if (combinedList.length >= 3 && !defaultSelectionSet.current) {
      defaultSelectionSet.current = true
      setSelectedIds(new Set(combinedList.slice(0, 3).map((item) => item.id)))
    }
  }, [combinedList])

  // Fetch call count per selected number for the current date range (reacts to period/time change)
  useEffect(() => {
    const startIso = dateFrom ? new Date(dateFrom + 'T00:00:00').toISOString() : ''
    const endIso = dateTo ? new Date(dateTo + 'T23:59:59.999').toISOString() : ''
    const selected = combinedList.filter((item) => selectedIds.has(item.id))
    const rType = getRangeType(dateFrom, dateTo)
    if (selected.length === 0 || !startIso || !endIso || !rType) {
      setCountsByItemBySlot({})
      return
    }
    let cancelled = false
    setChartDataLoading(true)
    const isTrue = (new Date(endIso) - new Date(startIso)) / (24 * 60 * 60 * 1000) < 31
    const filterBase = { timeStartStart: startIso, timeEndEnd: endIso }
    Promise.all(
      selected.map((item) => {
        const filter = item.type === 'group'
          ? { ...filterBase, number: item.number }
          : { ...filterBase, did: item.number }
        return Api.reportGraphic('status', isTrue, filter)
          .then((res) => ({ itemId: item.id, data: res?.data }))
          .catch(() => ({ itemId: item.id, data: null }))
      })
    ).then((results) => {
      if (cancelled) return
      const next = {}
      results.forEach(({ itemId, data }) => {
        next[itemId] = {}
        const buckets = data?.buckets
        if (Array.isArray(buckets)) {
          buckets.forEach((b) => {
            const count = getBucketCount(b)
            let slotKey = null
            if (rType === 'hour') {
              const h = bucketValToHour(b.val)
              if (h != null && h >= 0 && h <= 23) slotKey = String(h)
            } else if (rType === 'day') {
              slotKey = bucketValToDateStr(b.val)
            } else if (rType === 'month') {
              slotKey = bucketValToMonthStr(b.val)
            }
            if (slotKey) next[itemId][slotKey] = (next[itemId][slotKey] || 0) + count
          })
        }
      })
      setCountsByItemBySlot(next)
    }).finally(() => {
      if (!cancelled) setChartDataLoading(false)
    })
    return () => { cancelled = true }
  }, [dateFrom, dateTo, selectedIds, combinedList])

  const filteredList = useMemo(() => {
    if (!searchQuery || !searchQuery.trim()) return combinedList
    const q = searchQuery.trim().toLowerCase()
    return combinedList.filter(
      (item) =>
        (item.label && String(item.label).toLowerCase().includes(q)) ||
        (item.number && String(item.number).toLowerCase().includes(q)) ||
        (item.id && String(item.id).toLowerCase().includes(q))
    )
  }, [combinedList, searchQuery])

  const chartLabelsAndSlots = useMemo(() => {
    const rType = rangeType
    if (rType === 'hour') {
      return getHourSlotsAndLabels()
    }
    if (rType === 'day') {
      const days = getDaysInRange(dateFrom, dateTo)
      return {
        labels: days.map((d) => {
          const [, m, day] = d.split('-')
          return `${day}/${m}`
        }),
        slots: days,
      }
    }
    if (rType === 'month') {
      const months = getMonthsInRange(dateFrom, dateTo)
      return { labels: months.map((m) => m.label), slots: months.map((m) => m.key) }
    }
    return { labels: [], slots: [] }
  }, [rangeType, dateFrom, dateTo])

  const lineChartData = useMemo(() => {
    const { labels, slots } = chartLabelsAndSlots
    if (slots.length === 0) return { labels: [], datasets: [] }
    const selectedItems = combinedList.filter((item) => selectedIds.has(item.id))
    const datasets = selectedItems.map((item) => ({
      label: item.label || item.number || item.id,
      data: slots.map((slot) => (countsByItemBySlot[item.id] && countsByItemBySlot[item.id][slot]) || 0),
      borderColor: item.color,
      backgroundColor: `${item.color}20`,
      fill: false,
      tension: 0.3,
      pointBackgroundColor: item.color,
      pointBorderColor: item.color,
    }))
    return {
      labels,
      datasets,
    }
  }, [selectedIds, chartLabelsAndSlots, combinedList, countsByItemBySlot])

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    )
  }

  return (
    <Box sx={{ ...opStyles.sectionBox, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {opTr.loadNumbersError?.[langIndex] ?? 'Failed to load numbers'}
        </Alert>
      )}
      {isDemoData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {opTr.demoData?.[langIndex] ?? 'Showing demo data.'}
        </Alert>
      )}

      {/* Line graph: total call count of checked numbers (uses dateFrom/dateTo from period selector) */}
      <Paper sx={{ ...opStyles.chartPaperWide, mb: 2 }}>
        <Typography variant="subtitle1" sx={opStyles.chartTitle}>
          {opTr?.callCountChart?.[langIndex] ?? 'Call count (selected numbers)'}
        </Typography>
        <Box sx={{ ...opStyles.chartBox, position: 'relative' }}>
          {chartDataLoading && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1 }}>
              <CircularProgress size={32} sx={{ color: greenTheme.primary }} />
            </Box>
          )}
          {selectedIds.size > 0 ? (
            chartLabelsAndSlots.slots.length > 0 ? (
              <Line
                data={lineChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: { mode: 'index', intersect: false },
                  plugins: {
                    legend: { position: 'top' },
                    tooltip: { mode: 'index', intersect: false },
                  },
                  scales: {
                    x: {
                      grid: { display: false },
                    },
                    y: {
                      beginAtZero: true,
                      ticks: { stepSize: 1 },
                    },
                  },
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {opTr?.selectPeriodForChart?.[langIndex] ?? 'Select a date range above to see call count by day.'}
              </Typography>
            )
          ) : (
            <Typography variant="body2" color="text.secondary">
              {opTr?.selectNumbersForChart?.[langIndex] ?? 'Select numbers below to see total call count.'}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Number list with checkboxes and colors */}
      <Paper sx={opStyles.chartPaperWide}>
        <Typography variant="subtitle1" sx={opStyles.chartTitle}>
          {opTr.mainNumbersTitle?.[langIndex] ?? 'Main numbers'}
        </Typography>
        <Box sx={{ mb: 2, width: '100%' }}>
          <AssignedSearch value={searchQuery} onChange={setSearchQuery} fullWidth />
        </Box>
        {combinedList.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {opTr.noNumbers?.[langIndex] ?? 'No numbers'}
          </Typography>
        ) : filteredList.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {opTr?.noNumbersMatch?.[langIndex] ?? 'No numbers found matching your search.'}
          </Typography>
        ) : (
          <List dense disablePadding>
            {filteredList.map((item) => (
              <ListItem key={item.id} divider disablePadding>
                <ListItemButton onClick={() => toggleSelected(item.id)} sx={{ py: 1 }} dense>
                  <Checkbox
                    checked={selectedIds.has(item.id)}
                    onChange={() => toggleSelected(item.id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: item.color, '&.Mui-checked': { color: item.color }, p: 0.5, mr: 1 }}
                  />
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: 0.5,
                      bgcolor: item.color,
                      mr: 1.5,
                      flexShrink: 0,
                    }}
                  />
                  <ListItemText
                    primary={item.label || item.number || '—'}
                    secondary={item.label ? item.number : null}
                  />
                  {item.type === 'did' && (item.provider === 3 || item.provider === 4) && (
                    <TelegramIcon sx={{ fontSize: 20, color: '#0088cc', ml: 1 }} />
                  )}
                  {item.type === 'did' && item.provider === 7 && (
                    <WhatsAppIcon sx={{ fontSize: 20, color: '#25D366', ml: 1 }} />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  )
}
